<template>
  <div :class="$style.itemTableContainer">
    <div :class="$style.header">
      <h2>
        {{ totalCount }}
        {{ headerText }}
      </h2>
      <p>
        These system(s) require end user interaction.
        <ExternalLink
          :href="KbArticleUrls.usingSecureTokens"
          :showExternalLinkIcon="false"
          text="Learn More"
        />
      </p>
    </div>
    <ItemTable
      ref="needsAttentionSystems"
      :boundItemsUrl="boundItemsUrl"
      :columns="columns"
      :details="true"
      :disableBoundCount="true"
      :handleBulkDelete="handleBulkDelete"
      :handleDetailsButtonClick="handleDetailsButtonClick"
      :handleRowClick="handleRowClick"
      :hasSelectColumn="true"
      :inDetailsAsidePanel="false"
      :inMainPanel="true"
      :isAsideOpen="isAsideOpen"
      :itemType="itemTableType"
      :keepFresh="true"
      :selectedModelId="selectedModelId"
      :setSelectedModelId="setSelectedModelId"
      :showActionBar="true"
      :showBulkAction="false"
      :showBulkDelete="true"
      :showSearchForm="false"
      :viewport="viewport"
    />
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import ExternalLink from 'client/components/ExternalLink.vue';
import ItemTable from 'components/ItemTable/ItemTable.vue';
import ItemTableIndirectBindingMixin from 'components/ItemTable/mixins/ItemTableIndirectBindingMixin';
import ItemTableMethodsMixin from 'components/ItemTable/mixins/ItemTableMethodsMixin';
import ItemTablePropsMixin from 'components/ItemTable/mixins/ItemTablePropsMixin';
import ItemType from 'client/util/Constants/ItemType';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'components/Modal/ModalEventBus';
import RouterService from 'client/util/RouterService';
import SystemDeleteModal from 'components/Modal/SystemDeleteModal.vue';
import SystemItemTableColumns from 'client/systems/SystemItemTable/SystemItemTableColumns';
import pluralize from 'pluralize';

const {
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeRoutes,
  ModuleConsts,
  StoreModuleMap,
  error,
  success,
} = Constants;

const { DeviceItemType } = ItemType;
const deviceItem = new DeviceItemType();

const systemNeedsAttentionCollection = StoreModuleMap[ItemTypeConsts.systemNeedsAttention];
const { systemsCouldNotBeDeleted } = ModuleConsts.system.errors;
export default {

  name: 'NeedsAttentionItemTable',

  components: {
    ExternalLink,
    ItemTable,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    ItemTableIndirectBindingMixin,
    ItemTableMethodsMixin,
    ItemTablePropsMixin,
  ],
  ModalEventBus,
  notify,

  props: {
    itemType: {
      type: String,
      default: ItemTypeConsts.device,
    },
    panelType: {
      default: ItemTableConsts.panelType.mainPanel,
      type: String,
    },
  },

  data() {
    const columns = [
      SystemItemTableColumns.status,
      SystemItemTableColumns.name,
      SystemItemTableColumns.os,
      SystemItemTableColumns.lastContact,
      SystemItemTableColumns.hasServiceAccount,
      SystemItemTableColumns.validSecureToken,
      SystemItemTableColumns.validODPassword,
      SystemItemTableColumns.passwordAPFSValid,
    ];

    return {
      columns,
      itemTableType: ItemTypeConsts.systemNeedsAttention,
      inMainPanel: true,
      KbArticleUrls,
      route: ItemTypeRoutes[ItemTypeConsts.deviceList],
      systemItemText: {
        singular: deviceItem.title,
        plural: deviceItem.titlePlural,
      },
    };
  },

  computed: {
    ...mapGetters(systemNeedsAttentionCollection, {
      items: 'getCollection',
      totalCount: 'getTotalCount',
      selectedItems: 'getSelectedItems',
    }),

    headerText: {
      get() {
        return this.totalCount === 1
          ? `${this.systemItemText.singular} Needs Your Attention` : `${this.systemItemText.plural} Need Your Attention`;
      },
    },
  },

  beforeCreate() {
    const { parentType } = this.$options.propsData;
    this.$options.methods.handleBeforeCreate(this.$options, systemNeedsAttentionCollection);

    if (parentType === ItemTypeConsts.systemUser) {
      this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options, true);
    }
  },

  methods: {
    ...mapActions(systemNeedsAttentionCollection, [
      'deleteItems',
    ]),

    deleteSelectedSystems() {
      this.$options.ModalEventBus.close();
      const itemsCount = this.selectedItems.length;
      this.deleteItems(this.selectedItems)
        .then((result) => {
          this.notifyDeleteItems(result, itemsCount);
        })
        .catch(() => {
          this.$options.notify(error, systemsCouldNotBeDeleted);
        });
    },

    handleBulkDelete() {
      // close aside panel otherwise aside panel for deleted system will be open after delete
      this.closeAsidePanel();
      this.showDeleteModal();
    },
    handleRowClick(itemId) {
      if (this.inMainPanel) {
        RouterService.redirect({
          path: `${ItemTypeRoutes[ItemTypeConsts.device]}/${itemId}/details`,
          viewport: this.viewport,
          vueRouter: this.$router,
        });
      }
    },

    notifyDeleteItems(res, itemsCount) {
      const message = res.deletedIds.length > 0 ? success : error;
      return this.$options.notify(message, `${res.deletedIds.length} of ${pluralize(deviceItem.title, itemsCount, true)} ${pluralize('has', itemsCount)} been successfully deleted.`);
    },

    showDeleteModal() {
      const selectedCount = this.selectedItems.length;

      if (!selectedCount) {
        return;
      }

      this.$options.ModalEventBus.open({
        component: SystemDeleteModal,
        componentProps: {
          action: this.deleteSelectedSystems,
          itemType: ItemTypeConsts.systemNeedsAttention,
        },
      });
    },
  },
};
</script>

<style module>
@value (jcWhite) from '~css/brand-colors.css';
@value (gradientLight3) from '~css/colors.css';

.header {
  text-align: center;
}

.header h2 {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 0;
}

.itemTableContainer {
  background: jcWhite;
  overflow: auto;
  width: 100%;
}
</style>
